import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ItemDTO,
  ItemTipo,
  CreateItemRequest,
  UpdateItemRequest,
} from '@central/types';
import { api } from '@/lib/api';

interface ListItemsResponse {
  items: ItemDTO[];
  total: number;
}

export function useItemsList(filters: { tipo?: ItemTipo; status?: string; limit?: number } = {}): UseQueryResult<ListItemsResponse> {
  const qs = new URLSearchParams();
  if (filters.tipo) qs.set('tipo', filters.tipo);
  if (filters.status) qs.set('status', filters.status);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const qstr = qs.toString();
  return useQuery({
    queryKey: ['items', filters],
    queryFn: () => api<ListItemsResponse>(`/api/items${qstr ? '?' + qstr : ''}`),
  });
}

export function useItem(id: number | null): UseQueryResult<ItemDTO> {
  return useQuery({
    queryKey: ['items', 'detail', id],
    queryFn: () => api<ItemDTO>(`/api/items/${id}`),
    enabled: id !== null,
  });
}

export function useCreateItem(): ReturnType<typeof useMutation<ItemDTO, Error, CreateItemRequest>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateItemRequest) => api<ItemDTO>('/api/items', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

export function useUpdateItem(
  id: number,
): ReturnType<typeof useMutation<ItemDTO, Error, UpdateItemRequest>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateItemRequest) =>
      api<ItemDTO>(`/api/items/${id}`, { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.setQueryData(['items', 'detail', id], data);
    },
  });
}

export function useDeleteItem(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/items/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

export interface ItemVersion {
  id: number;
  item_id: number;
  user_id: number | null;
  diff: Record<string, { from: unknown; to: unknown }>;
  motivo: string;
  criado_em: string;
}

export function useItemVersions(
  id: number | null,
): UseQueryResult<{ versions: ItemVersion[]; total: number }> {
  return useQuery({
    queryKey: ['items', 'versions', id],
    queryFn: () => api<{ versions: ItemVersion[]; total: number }>(`/api/items/${id}/versions`),
    enabled: id !== null,
  });
}
